import { useCurrentZap } from "../../../components/ZapContext";
import { useAccountActionPermissions } from "../useAccountActionPermissions";
import { useUserAccountRole } from "../useUserAccountRole";
import { useFolderZapActionPermissions } from "./useFolderZapActionPermissions";
import { useProjectZapActionPermissions } from "./useProjectZapActionPermissions";
import { Zap } from "../../zap/useSearchZaps";
import { HydratedZapAsset } from "@zapier/bff";

/**
 * ⚠️ This component needs to have access to ZapContext.Provider to have context
 * on the current Zap.
 */

export interface Response {
  canChangeOwner: boolean;
  canCreateTemplate: boolean;
  canDelete: boolean;
  canDuplicate: boolean;
  canEdit: boolean;
  canEnableDisable: boolean;
  canExport: boolean;
  canMoveZapToFolder: boolean;
  canMoveZapToProject: boolean;
  canRename: boolean;
  canShare: boolean;
  canShowDrafts: boolean;
  canViewZapHistory: boolean;
  slackWorkflowBuilderUrl?: string;
  enableAdvancedPermissions?: () => void;
  // User messaging
  disabledCreateZapMessage?: string;
  disabledDuplicateZapMessage?: string;
}

interface Input {
  enabled?: boolean;
  // Optionally include a Zap to avoid using the context
  zap?: Zap | HydratedZapAsset | null;
  // Optionally include the Zap UUID if not using context
  zapUUID?: string;
}

export const defaultResponse: Response = {
  canChangeOwner: false,
  canCreateTemplate: false,
  canDelete: false,
  canDuplicate: false,
  canEdit: false,
  canEnableDisable: false,
  canExport: false,
  canMoveZapToFolder: false,
  canMoveZapToProject: false,
  canRename: false,
  canShare: false,
  canShowDrafts: false,
  canViewZapHistory: true,
  enableAdvancedPermissions: undefined,
  slackWorkflowBuilderUrl: "",
};

export function useZapActionPermissions(input?: Input): Response {
  const { enabled = true, zap: zapArgument, zapUUID } = input || {};

  // grab the current Zap from the nearest ZapContext.Provider
  const zapContext = useCurrentZap();
  const zap = zapArgument || zapContext;

  const { customuserId, isAccountOwner, isSuperAdmin, isAdmin } =
    useUserAccountRole();
  const { isImportExportEnabled } = useAccountActionPermissions();

  // if this Zap does not have a projectId, calculate folder permissions
  // else, return null
  const folderPermissions = useFolderZapActionPermissions({
    zap,
    customuserId,
    isAccountOwner,
    isSuperAdmin,
    isAdmin,
    isImportExportEnabled,
    enabled,
  });

  // if this Zap has a projectId, calculate project permissions
  // else, return null
  const projectPermissions = useProjectZapActionPermissions({
    zapUUID,
    zap,
    isImportExportEnabled,
    enabled,
  });

  return projectPermissions || folderPermissions || defaultResponse;
}
